import { ChevronRight } from 'lucide-react'
import { Tooltip } from './Tooltip'

/** Breadcrumb segment; a truncated name shows its full text on the shared `<Tooltip>` (overflow-only).
 *  Renders as `<span>` by default, or `<button>` when onClick is provided.
 *  `className` overrides the default truncation styling — useful for
 *  compact contexts where the default `max-w-[80px]` cap is wrong. */
export function BreadcrumbSegment({
  name,
  isLast,
  onClick,
  className,
}: {
  name: string
  isLast: boolean
  onClick?: () => void
  className?: string
}) {
  const classes = className ?? (isLast ? 'themed-text truncate' : `truncate max-w-[80px] ${onClick ? 'hover:themed-text cursor-pointer' : ''}`)
  // #1426: the Tooltip owns the touch rule — a tap inside the button is `insideAction`, so it navigates and shows nothing
  if (onClick) {
    return (
      <button onClick={onClick} className={classes}>
        <Tooltip content={name} className="block truncate">{name}</Tooltip>
      </button>
    )
  }
  return <Tooltip content={name} placement="bottom" className={classes}>{name}</Tooltip>
}

/** Path-based breadcrumbs (display only, no click navigation).
 *
 *  Props:
 *  - `path`: forward-slash path; leading `P:/` is stripped.
 *  - `separator`: node rendered between segments. Defaults to a
 *    ChevronRight icon. Pass a string (e.g. `'/'`) for a slash-style
 *    path, or any ReactNode for custom separators.
 *  - `trailingSeparator`: when true, also renders the separator AFTER
 *    the final segment. Useful when the breadcrumb is shown as a
 *    path-prefix above a filename — the trailing slash makes the
 *    parent relationship visually explicit. Also suppresses the
 *    "last segment is highlighted" styling, since the visible chain
 *    no longer ends on a segment.
 *  - `compact`: tighter inter-segment gap for inline / list contexts.
 */
export function BreadcrumbPath({
  path,
  separator,
  trailingSeparator = false,
  compact = false,
}: {
  path: string
  separator?: React.ReactNode
  trailingSeparator?: boolean
  compact?: boolean
}) {
  const segments = path.replace(/^P:\//, '').split('/').filter(Boolean)
  const sep = separator !== undefined
    ? separator
    : <ChevronRight size={12} className="themed-text-muted shrink-0" />
  const gap = compact ? 'gap-0.5' : 'gap-1'
  return (
    <div className={`flex items-center ${gap} min-w-0 shrink overflow-hidden`}>
      {segments.map((segment, i) => (
        <span key={i} className={`flex items-center ${gap} min-w-0 shrink`}>
          {i > 0 && <span className="shrink-0">{sep}</span>}
          <BreadcrumbSegment
            name={segment}
            isLast={!trailingSeparator && i === segments.length - 1}
            // Compact: drop the default max-w-[80px] cap (which leaves
            // visible empty space inside the truncate box) and use
            // min-w-0 + a tighter cap so the box hugs rendered content.
            // The portal-tooltip-on-overflow behavior comes for free
            // from BreadcrumbSegment.
            className={compact
              ? 'truncate min-w-0 max-w-[8rem]'
              : undefined}
          />
        </span>
      ))}
      {trailingSeparator && segments.length > 0 && (
        <span className="shrink-0">{sep}</span>
      )}
    </div>
  )
}

/** Navigable breadcrumb trail with click handlers.
 *  - `root`: label for the root crumb
 *  - `crumbs`: array of `{ id, name }` for each segment
 *  - `onNavigate(id | null)`: called when a crumb is clicked (null = root)
 */
export function Breadcrumbs({ root, crumbs, onNavigate }: {
  root: string
  crumbs: Array<{ id: string; name: string }>
  onNavigate: (id: string | null) => void
}) {
  return (
    <div className="flex items-center gap-1 min-w-0 shrink overflow-hidden">
      <BreadcrumbSegment name={root} isLast={crumbs.length === 0} onClick={() => onNavigate(null)} />
      {crumbs.map((crumb, i) => (
        <span key={crumb.id} className="flex items-center gap-1 min-w-0 shrink">
          <ChevronRight size={12} className="themed-text-muted shrink-0" />
          <BreadcrumbSegment name={crumb.name} isLast={i === crumbs.length - 1} onClick={() => onNavigate(crumb.id)} />
        </span>
      ))}
    </div>
  )
}
